import { useState, useEffect } from 'react';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { MonthlyStats } from '../../types/property';

interface MonthlyDataFormProps {
  propertyId: string;
  initialData?: MonthlyStats | null;
  onSubmit: (data: Omit<MonthlyStats, 'id' | 'created_at'>) => Promise<void>;
}

export const MonthlyDataForm = ({ propertyId, initialData, onSubmit }: MonthlyDataFormProps) => {
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    year: currentYear,
    month: new Date().getMonth() + 1,
    revenue: 0,
    cleaningCosts: 0,
    inmohostCosts: 0,
    profit: 0,
    occupancyRate: 0,
    reservations: 0,
    averageNightPrice: 0,
    averageStayDuration: 0
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        year: initialData.year,
        month: initialData.month,
        revenue: initialData.revenue,
        cleaningCosts: initialData.cleaningCosts,
        inmohostCosts: initialData.inmohostCosts,
        profit: initialData.profit,
        occupancyRate: initialData.occupancyRate,
        reservations: initialData.reservations,
        averageNightPrice: initialData.averageNightPrice,
        averageStayDuration: initialData.averageStayDuration
      });
    }
  }, [initialData]);

  const months = [
    { value: '1', label: 'Enero' },
    { value: '2', label: 'Febrero' },
    { value: '3', label: 'Marzo' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Mayo' },
    { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' }
  ];

  // Generar años desde 2023 hasta 5 años en el futuro
  const years = Array.from({ length: 8 }, (_, i) => ({
    value: String(2023 + i),
    label: String(2023 + i)
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSubmit({
        ...formData,
        propertyId
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateProfit = () => {
    const profit = formData.revenue - formData.cleaningCosts - formData.inmohostCosts;
    setFormData(prev => ({ ...prev, profit }));
  };

  useEffect(() => {
    calculateProfit();
  }, [formData.revenue, formData.cleaningCosts, formData.inmohostCosts]);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Año"
          value={String(formData.year)}
          onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })}
          options={years}
          required
        />
        <Select
          label="Mes"
          value={String(formData.month)}
          onChange={(e) => setFormData({ ...formData, month: Number(e.target.value) })}
          options={months}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Facturación (€)"
          type="number"
          value={formData.revenue}
          onChange={(e) => setFormData({ ...formData, revenue: Number(e.target.value) })}
          required
        />
        <Input
          label="Gastos Limpieza (€)"
          type="number"
          value={formData.cleaningCosts}
          onChange={(e) => setFormData({ ...formData, cleaningCosts: Number(e.target.value) })}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Gastos INMOHOST (€)"
          type="number"
          value={formData.inmohostCosts}
          onChange={(e) => setFormData({ ...formData, inmohostCosts: Number(e.target.value) })}
          required
        />
        <Input
          label="Beneficio (€)"
          type="number"
          value={formData.profit}
          readOnly
          className="bg-gray-50"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Ocupación (%)"
          type="number"
          min="0"
          max="100"
          value={formData.occupancyRate}
          onChange={(e) => setFormData({ ...formData, occupancyRate: Number(e.target.value) })}
          required
        />
        <Input
          label="Reservas"
          type="number"
          min="0"
          value={formData.reservations}
          onChange={(e) => setFormData({ ...formData, reservations: Number(e.target.value) })}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Precio Medio/Noche (€)"
          type="number"
          min="0"
          value={formData.averageNightPrice}
          onChange={(e) => setFormData({ ...formData, averageNightPrice: Number(e.target.value) })}
          required
        />
        <Input
          label="Duración Media Estancia (noches)"
          type="number"
          min="0"
          value={formData.averageStayDuration}
          onChange={(e) => setFormData({ ...formData, averageStayDuration: Number(e.target.value) })}
          required
        />
      </div>

      <div className="flex justify-end space-x-3">
        <Button
          type="submit"
          loading={loading}
        >
          {initialData ? 'Actualizar' : 'Guardar'}
        </Button>
      </div>
    </form>
  );
};